import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Box,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Stack,
  Button,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import ScienceIcon from '@mui/icons-material/Science';
import HistoryIcon from '@mui/icons-material/History';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import ArchiveIcon from '@mui/icons-material/Archive';
import UndoIcon from '@mui/icons-material/Undo';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AxiosError } from 'axios';
import { toast } from 'react-toastify';

import {
  fetchQueueStrategies,
  createQueueStrategy,
  updateQueueStrategy,
  publishQueueStrategy,
  QueueStrategy,
  QueueStrategyRequest,
} from '../../api/queueStrategies';
import { fetchQueueOptions, QueueListItem } from '../../api/queues';
import StrategyFormDrawer, { QueueOption } from './components/StrategyFormDrawer';
import DryRunDialog from './components/DryRunDialog';
import AuditLogDialog from './components/AuditLogDialog';

interface StrategyPreview extends QueueStrategy {
  author?: string;
}

type StatusFilter = 'all' | 'draft' | 'active' | 'archived';

const statusTitle: Record<QueueStrategy['status'], string> = {
  draft: 'Черновик',
  active: 'Активна',
  archived: 'Архив',
};

const statusColor: Record<QueueStrategy['status'], 'default' | 'success' | 'warning'> = {
  draft: 'default',
  active: 'success',
  archived: 'warning',
};

const modeTitle: Record<QueueStrategy['mode'], string> = {
  scoring: 'Взвешивание',
  rule_order: 'Правила по порядку',
  hybrid: 'Гибрид',
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '—';
  }
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch (error) {
    return value;
  }
};

const formatQueues = (strategy: QueueStrategy) => {
  if (!strategy.assignments || strategy.assignments.length === 0) {
    return 'Очереди не привязаны';
  }
  return strategy.assignments
    .map((assignment) => assignment.queueName || assignment.queueId)
    .filter(Boolean)
    .join(', ');
};

const mapQueuesToOptions = (queues: QueueListItem[]): QueueOption[] =>
  queues.map((queue) => ({
    id: queue.id,
    name: queue.name,
    code: queue.code,
  }));

const QueueSettingsPage: React.FC = () => {
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<QueueStrategy | null>(null);

  const [dryRunStrategy, setDryRunStrategy] = useState<QueueStrategy | null>(null);
  const [auditStrategy, setAuditStrategy] = useState<QueueStrategy | null>(null);

  const [pendingStatus, setPendingStatus] = useState<{ id: string; status: QueueStrategy['status'] } | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery(['queue-strategies'], fetchQueueStrategies, {
    staleTime: 30_000,
    retry: (failureCount, requestError) => {
      const axiosError = requestError as AxiosError;
      if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const strategies = useMemo(
    () => ((data?.strategies ?? []) as StrategyPreview[]),
    [data?.strategies],
  );
  const capabilities = data?.capabilities ?? [];

  const shouldLoadQueues = isDrawerOpen || Boolean(dryRunStrategy);
  const { data: queueOptionsData } = useQuery(
    ['queues', { limit: 100 }],
    () => fetchQueueOptions({ limit: 100, status: 'active' }),
    {
      enabled: shouldLoadQueues,
      staleTime: 5 * 60 * 1000,
    },
  );

  const assignmentQueues = useMemo(() => {
    const map = new Map<string, QueueOption>();
    strategies.forEach((strategy) => {
      strategy.assignments?.forEach((assignment) => {
        if (!map.has(assignment.queueId)) {
          map.set(assignment.queueId, {
            id: assignment.queueId,
            name: assignment.queueName || assignment.queueId,
          });
        }
      });
    });
    return Array.from(map.values());
  }, [strategies]);

  const queueOptions = useMemo(() => {
    const map = new Map<string, QueueOption>();
    assignmentQueues.forEach((queue) => map.set(queue.id, queue));
    mapQueuesToOptions(queueOptionsData ?? []).forEach((queue) => {
      map.set(queue.id, queue);
    });
    return Array.from(map.values());
  }, [assignmentQueues, queueOptionsData]);

  const createStrategyMutation = useMutation((payload: QueueStrategyRequest) => createQueueStrategy(payload));
  const updateStrategyMutation = useMutation((variables: { id: string; payload: QueueStrategyRequest }) =>
    updateQueueStrategy(variables.id, variables.payload),
  );
  const changeStatusMutation = useMutation((variables: { id: string; status: QueueStrategy['status'] }) =>
    publishQueueStrategy(variables.id, variables.status),
  );

  const filteredStrategies = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    return strategies.filter((strategy) => {
      const matchesStatus = statusFilter === 'all' || strategy.status === statusFilter;
      const matchesSearch =
        normalizedSearch.length === 0 ||
        strategy.name.toLowerCase().includes(normalizedSearch) ||
        (strategy.description ?? '').toLowerCase().includes(normalizedSearch) ||
        strategy.assignments?.some((assignment) =>
          (assignment.queueName || assignment.queueId).toLowerCase().includes(normalizedSearch),
        );
      return matchesStatus && matchesSearch;
    });
  }, [statusFilter, strategies, search]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredStrategies.length / rowsPerPage)), [
    filteredStrategies.length,
    rowsPerPage,
  ]);

  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, rowsPerPage]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const paginatedStrategies = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredStrategies.slice(start, start + rowsPerPage);
  }, [filteredStrategies, page, rowsPerPage]);

  const handleOpenCreate = () => {
    setEditingStrategy(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (strategy: QueueStrategy) => {
    setEditingStrategy(strategy);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    if (createStrategyMutation.isLoading || updateStrategyMutation.isLoading) {
      return;
    }
    setIsDrawerOpen(false);
    setEditingStrategy(null);
  };

  const handleSubmitStrategy = async (payload: QueueStrategyRequest) => {
    try {
      if (editingStrategy) {
        await updateStrategyMutation.mutateAsync({ id: editingStrategy.id, payload });
        toast.success('Стратегия обновлена');
      } else {
        await createStrategyMutation.mutateAsync(payload);
        toast.success('Стратегия создана');
      }
      setIsDrawerOpen(false);
      setEditingStrategy(null);
      queryClient.invalidateQueries(['queue-strategies']);
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string; errors?: Array<{ msg: string }> }>;
      const message =
        axiosError.response?.data?.errors?.[0]?.msg ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        'Не удалось сохранить стратегию';
      throw new Error(message);
    }
  };

  const handleChangeStatus = async (strategy: QueueStrategy, status: QueueStrategy['status']) => {
    setPendingStatus({ id: strategy.id, status });
    try {
      await changeStatusMutation.mutateAsync({ id: strategy.id, status });
      const statusLabel = statusTitle[status];
      toast.success(`Статус стратегии «${strategy.name}» изменён на «${statusLabel}»`);
      queryClient.invalidateQueries(['queue-strategies']);
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const message = axiosError.response?.data?.message || axiosError.message || 'Не удалось изменить статус стратегии';
      toast.error(message);
    } finally {
      setPendingStatus(null);
    }
  };

  const errorMessage = useMemo(() => {
    if (!error) {
      return null;
    }
    const axiosError = error as AxiosError<{ message?: string }>;
    if (axiosError.response?.status === 403) {
      return 'Недостаточно прав для просмотра стратегий очередей.';
    }
    if (axiosError.response?.status === 401) {
      return 'Сессия истекла. Перезайдите в систему.';
    }
    return axiosError.response?.data?.message || axiosError.message || 'Не удалось загрузить стратегии.';
  }, [error]);

  const isStatusLoading = (strategyId: string, status: QueueStrategy['status']) =>
    Boolean(
      pendingStatus &&
        pendingStatus.id === strategyId &&
        pendingStatus.status === status &&
        changeStatusMutation.isLoading,
    );

  return (
    <>
      <Helmet>
        <title>Стратегии очередей | ELEKTROM</title>
      </Helmet>

      <Box
        sx={{
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
        }}
      >
        <div>
          <Typography variant="h4" gutterBottom>
            Стратегии очередей
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Управляйте правилами приоритизации, назначайте стратегии очередям и отслеживайте изменения.
          </Typography>
        </div>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Обновляем…' : 'Обновить'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreate}>
            Создать стратегию
          </Button>
        </Stack>
      </Box>

      <Stack spacing={2} sx={{ mb: 3 }}>
        {isError && errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {isFetching && !isLoading && !isError && (
          <Alert severity="info">Обновляем данные…</Alert>
        )}
      </Stack>

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardHeader
              title="Стратегии"
              subheader={isLoading ? 'Загружаем…' : `Всего: ${strategies.length}`}
              action={
                <TextField
                  select
                  size="small"
                  label="На странице"
                  value={rowsPerPage}
                  onChange={(event) => setRowsPerPage(Number(event.target.value))}
                  sx={{ width: 140 }}
                >
                  {[5, 10, 20].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              }
            />
            <CardContent>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <TextField
                  placeholder="Поиск по названию, описанию или очередям"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                  size="small"
                  fullWidth
                />

                <ToggleButtonGroup
                  value={statusFilter}
                  exclusive
                  size="small"
                  onChange={(_, value) => {
                    if (value !== null) {
                      setStatusFilter(value);
                    }
                  }}
                >
                  <ToggleButton value="all">Все</ToggleButton>
                  <ToggleButton value="active">Активные</ToggleButton>
                  <ToggleButton value="draft">Черновики</ToggleButton>
                  <ToggleButton value="archived">Архив</ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              {isLoading ? (
                <Stack direction="row" spacing={2} alignItems="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2">Загружаем данные…</Typography>
                </Stack>
              ) : paginatedStrategies.length === 0 ? (
                <Stack spacing={1} alignItems="center" sx={{ py: 6 }}>
                  <InfoOutlinedIcon color="disabled" fontSize="large" />
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    {strategies.length === 0
                      ? 'Стратегии ещё не созданы. Нажмите «Создать стратегию», чтобы начать использовать модуль.'
                      : 'Нет стратегий, удовлетворяющих выбранным фильтрам.'}
                  </Typography>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  {paginatedStrategies.map((strategy) => (
                    <Card
                      key={strategy.id}
                      variant="outlined"
                      sx={{ borderColor: strategy.status === 'active' ? 'success.light' : undefined }}
                    >
                      <CardHeader
                        title={
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="h6">{strategy.name}</Typography>
                            <Chip label={modeTitle[strategy.mode]} size="small" variant="outlined" />
                          </Stack>
                        }
                        subheader={`Обновлена: ${formatDateTime(strategy.updatedAt)}`}
                        action={<Chip label={statusTitle[strategy.status]} color={statusColor[strategy.status]} size="small" />}
                      />
                      <CardContent>
                        <Stack spacing={1.5}>
                          {strategy.description && (
                            <Typography variant="body2" color="text.secondary">
                              {strategy.description}
                            </Typography>
                          )}

                          <Typography variant="body2">Очереди: {formatQueues(strategy)}</Typography>
                          <Typography variant="body2">Правил: {strategy.rules?.length ?? 0}</Typography>
                          <Typography variant="body2">Базовый score: {strategy.defaultPriorityScore}</Typography>

                          {strategy.assignments && strategy.assignments.length > 0 && (
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                              {strategy.assignments.map((assignment) => (
                                <Chip
                                  key={assignment.id}
                                  size="small"
                                  label={assignment.queueName || assignment.queueId}
                                  variant={assignment.isPrimary ? 'filled' : 'outlined'}
                                  color={assignment.isPrimary ? 'primary' : 'default'}
                                />
                              ))}
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => handleEdit(strategy)}
                          >
                            Редактировать
                          </Button>
                          <Button
                            size="small"
                            startIcon={<ScienceIcon />}
                            onClick={() => setDryRunStrategy(strategy)}
                          >
                            Симулировать
                          </Button>
                          <Button
                            size="small"
                            startIcon={<HistoryIcon />}
                            onClick={() => setAuditStrategy(strategy)}
                          >
                            Аудит
                          </Button>
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          {strategy.status !== 'active' && (
                            <LoadingButton
                              size="small"
                              variant="contained"
                              color="success"
                              startIcon={<RocketLaunchIcon />}
                              loading={isStatusLoading(strategy.id, 'active')}
                              onClick={() => handleChangeStatus(strategy, 'active')}
                            >
                              Активировать
                            </LoadingButton>
                          )}
                          {strategy.status !== 'draft' && (
                            <LoadingButton
                              size="small"
                              variant="outlined"
                              startIcon={<UndoIcon />}
                              loading={isStatusLoading(strategy.id, 'draft')}
                              onClick={() => handleChangeStatus(strategy, 'draft')}
                            >
                              В черновик
                            </LoadingButton>
                          )}
                          {strategy.status !== 'archived' && (
                            <LoadingButton
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<ArchiveIcon />}
                              loading={isStatusLoading(strategy.id, 'archived')}
                              onClick={() => handleChangeStatus(strategy, 'archived')}
                            >
                              В архив
                            </LoadingButton>
                          )}
                        </Stack>
                      </CardActions>
                    </Card>
                  ))}
                </Stack>
              )}
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Страница {page} из {totalPages}
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
                size="small"
              />
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Card variant="outlined">
              <CardHeader title="Возможности" subheader="Что предоставляет модуль" />
              <CardContent>
                {capabilities.length > 0 ? (
                  <Stack spacing={1}>
                    {capabilities.map((capability) => (
                      <Stack key={capability} direction="row" spacing={1} alignItems="center">
                        <Chip label="" size="small" color="primary" />
                        <Typography variant="body2">{capability}</Typography>
                      </Stack>
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Дополнительные возможности будут появляться по мере развития модуля.
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardHeader title="Советы по работе" />
              <CardContent>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <InfoOutlinedIcon fontSize="small" color="primary" sx={{ mt: '2px' }} />
                    <Typography variant="body2" color="text.secondary">
                      Используйте симуляцию, чтобы проверять влияние новых правил до публикации.
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <InfoOutlinedIcon fontSize="small" color="primary" sx={{ mt: '2px' }} />
                    <Typography variant="body2" color="text.secondary">
                      Архивируйте неиспользуемые стратегии — вы сможете вернуть их в работу в любой момент.
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <InfoOutlinedIcon fontSize="small" color="primary" sx={{ mt: '2px' }} />
                    <Typography variant="body2" color="text.secondary">
                      Следите за журналом аудита, чтобы отслеживать изменения и действия операторов.
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <StrategyFormDrawer
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        queues={queueOptions}
        isSubmitting={createStrategyMutation.isLoading || updateStrategyMutation.isLoading}
        initialStrategy={editingStrategy}
        onSubmit={handleSubmitStrategy}
      />

      <DryRunDialog
        open={Boolean(dryRunStrategy)}
        strategy={dryRunStrategy}
        queues={queueOptions}
        onClose={() => setDryRunStrategy(null)}
      />

      <AuditLogDialog
        open={Boolean(auditStrategy)}
        strategy={auditStrategy}
        onClose={() => setAuditStrategy(null)}
      />
    </>
  );
};

export default QueueSettingsPage;
